import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ADD_REACTION, REMOVE_REACTION, GET_REACTIONS } from '../../graphql/operations';
import { parseReactionCounts } from './reactionCounts';
import { normalizeReactionToken } from './reactionEmojiMap';

/**
 * useReactionToggle — single canonical reaction toggle used by both the
 * hover-toolbar quick-react chip (`MessageBubble`) and the existing pill
 * row (`ReactionBar`). KAP-309 follow-up.
 *
 * Why the indirection: AppSync's `addReaction` resolver is idempotent.
 * Sending AddReaction twice for the same {message, user, emoji} does NOT
 * throw — it silently no-ops and returns the unchanged `reactionCounts`.
 * The old try-add/catch-remove heuristic therefore never falls through to
 * REMOVE, and any optimistic increment is a phantom that won't survive a
 * refetch. Both call sites had this flaw; the chip was just more exposed.
 *
 * Approach:
 *   1. If the message has no count for this token, short-circuit to ADD
 *      (no need to ask who reacted with a reaction that doesn't exist).
 *   2. Otherwise lazy-fetch `getReactions` (Apollo `cache-first`) to find
 *      out whether the viewer is in the reactor list for this token.
 *   3. Dispatch ADD or REMOVE deterministically based on viewer state.
 *   4. Apply the server's returned `reactionCounts` straight into local
 *      state — never blindly increment.
 *
 * Returned function: `toggleReaction(token: string) => Promise<void>`.
 *
 * @param {object} args
 * @param {string} args.messageId
 * @param {string} args.conversationId
 * @param {?string} args.currentUserId  - viewer's user id; if null, we
 *   default to ADD (we can't ask "is the viewer in the reactor list" if
 *   we don't know who the viewer is).
 * @param {object|null} args.reactionCounts - parsed `{ token: count }`
 *   map of the current reaction state. May be null/empty.
 * @param {function} args.setReactionCounts - setter that accepts the new
 *   reaction counts object (or `null`). Can be a React `setState` setter
 *   or any callback with the same signature.
 */
export function useReactionToggle({
  messageId,
  conversationId,
  currentUserId,
  reactionCounts,
  setReactionCounts
}) {
  const [addReactionMutation] = useMutation(ADD_REACTION);
  const [removeReactionMutation] = useMutation(REMOVE_REACTION);
  const [fetchReactions] = useLazyQuery(GET_REACTIONS);

  const applyServerCounts = useCallback(
    (maybeCounts) => {
      const next = parseReactionCounts(maybeCounts);
      setReactionCounts(next || null);
    },
    [setReactionCounts]
  );

  return useCallback(
    async (token) => {
      const variables = { messageId, conversationId, emoji: token };

      // Step 1 — determine viewer state.
      const currentCount = (reactionCounts && reactionCounts[token]) || 0;
      let viewerHasReacted = false;
      if (currentCount > 0 && currentUserId) {
        try {
          const result = await fetchReactions({
            variables: { messageId },
            fetchPolicy: 'cache-first'
          });
          const reactions = result?.data?.getReactions || [];
          viewerHasReacted = reactions.some(
            (r) => r.userId === currentUserId
              && normalizeReactionToken(r.emoji) === token
          );
        } catch (err) {
          // If the lookup fails, default to ADD (the server will dedupe).
          // eslint-disable-next-line no-console
          console.warn('[useReactionToggle] could not read viewer reactions; defaulting to ADD', err);
        }
      }

      // Step 2 — dispatch the right mutation; sync local state from the
      // server's response so the UI never drifts from the truth.
      try {
        if (viewerHasReacted) {
          const result = await removeReactionMutation({ variables });
          applyServerCounts(result?.data?.removeReaction?.reactionCounts);
        } else {
          const result = await addReactionMutation({ variables });
          applyServerCounts(result?.data?.addReaction?.reactionCounts);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useReactionToggle] reaction toggle failed', err);
      }
    },
    [
      messageId,
      conversationId,
      currentUserId,
      reactionCounts,
      fetchReactions,
      addReactionMutation,
      removeReactionMutation,
      applyServerCounts
    ]
  );
}
